/* eslint-disable sonarjs/no-identical-functions -- all the "create music listing" functions should be identical */
import { Accordion, AccordionGroup } from '@nick-mazuk/ui/elements/accordion'
import { Box } from '@nick-mazuk/ui/elements/box'
import { Button } from '@nick-mazuk/ui/elements/button'
import { ArrowRight } from '@nick-mazuk/ui/elements/icon'
import { Image } from '@nick-mazuk/ui/elements/image'
import { Link } from '@nick-mazuk/ui/elements/link'
import { Ratio } from '@nick-mazuk/ui/elements/ratio'
import { Text } from '@nick-mazuk/ui/elements/text'
import { TextContent } from '@nick-mazuk/ui/elements/text-content'
import { CtaButtons } from '@nick-mazuk/ui/marketing/components/cta-buttons'
import { StatBar } from '@nick-mazuk/ui/marketing/components/stat-bar'
import type { SanityImageSource } from '@sanity/image-url/lib/types/types'
import { Layout } from 'components/layout'
import type { GetStaticProps } from 'next'

import type { Music, MusicCollection, MusicColor } from 'lib/sanity'
import { getAllMusic, getMusicCollection, imageBuilder } from 'lib/sanity'

// eslint-disable-next-line import/exports-last -- used in individual music pages
export const COLOR_MAP: Record<MusicColor, string> = {
    primary: 'bg-primary',
    success: 'bg-success',
    highlight: 'bg-highlight',
    error: 'bg-error',
    warning: 'bg-warning',
    gray: 'bg-gray',
    black: 'bg-black',
}

const buildImage = (imageRef: SanityImageSource): string => {
    if (imageRef) return `${imageBuilder.image(imageRef)}`
    return ''
}

const MusicItem = ({
    title,
    image,
    color,
    slug,
}: {
    title: string
    image?: string
    color: MusicColor
    slug: string
}): JSX.Element => {
    return (
        <Link className='flex flex-col space-y-2' href={`/music/${slug}`}>
            {image && <Image src={image} alt={title} ratio='3x2' rounded />}
            {!image && (
                <Ratio ratio='3x2'>
                    <div className={`${COLOR_MAP[color]} rounded-lg`} />
                </Ratio>
            )}
            <Text semibold truncate>
                {title}
            </Text>
        </Link>
    )
}

export default function MusicPage({
    collection,
    allMusic,
}: {
    collection: MusicCollection
    allMusic: Music[]
}): JSX.Element {
    let featuredMusic = ''
    return (
        <Layout title='Music'>
            <section className='mt-8 mb-24 overflow-hidden md:mt-16'>
                <div className='wrapper'>
                    <div className='flex flex-col space-y-8 text-center md:grid md:grid-cols-2 md:gap-16 md:text-left md:-mr-8 lg:-mr-54 xl:-mr-64'>
                        <div className='flex flex-col max-w-2xl space-y-8 sm:mt-4 lg:mt-16 xl:mt-32'>
                            <Text h1>Award-winning music</Text>
                            <Text>
                                Music should give you the feels. Experience the incredible fun many
                                ensembles across the country experience when they play this music.
                            </Text>
                            <CtaButtons
                                buttons={[
                                    {
                                        value: 'Shop Now',
                                        href: 'https://partsandscore.com/composer/nick-mazuk',
                                    },
                                    {
                                        value: 'Explore',
                                        href: '#explore',
                                    },
                                ]}
                            />
                        </div>
                        <div className='w-full'>
                            <Link href='/music/across-lots'>
                                <div className='overflow-hidden rounded-lg shadow-xl'>
                                    <Image
                                        src='/images/across-lots.png'
                                        alt='Across Lots'
                                        ratio='3x2'
                                        eager
                                    />
                                </div>
                                <div className='mt-8'>
                                    <Text semibold>Across Lots</Text>
                                    <span className='text-gray'>
                                        <Text small>
                                            Winner: Christopher Bill Composition Competition 2018
                                        </Text>
                                    </span>
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
            <StatBar
                stats={[
                    { value: '50+', label: 'Original pieces' },
                    { value: '100+', label: 'Arrangements' },
                    { value: '10+', label: 'Awards' },
                ]}
                color='primary'
            />
            <section id='explore' className='pt-40 pb-8 -mt-24 -mb-8 bg-gray-20'>
                <div className='wrapper'>
                    <div className='grid gap-6 md:grid-cols-4 lg:gap-8'>
                        <div className='col-span-4'>
                            <Box style='white' padding='large'>
                                <div className='grid items-center grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4 lg:gap-8'>
                                    <div className='flex flex-col space-y-2'>
                                        <Text h3>{collection.group[0].name}</Text>
                                        <div className='hidden sm:block'>
                                            <Button
                                                value='View more'
                                                style='text'
                                                glue={['left']}
                                                icon={<ArrowRight />}
                                                iconPosition='after'
                                                color='primary'
                                                href='#other'
                                            />
                                        </div>
                                    </div>
                                    {collection.group[0].pieces.map((piece) => {
                                        featuredMusic += piece.slug
                                        return (
                                            <MusicItem
                                                title={piece.title}
                                                key={piece.slug}
                                                color={piece.color}
                                                slug={piece.slug}
                                                image={buildImage(piece.image)}
                                            />
                                        )
                                    })}
                                </div>
                            </Box>
                        </div>
                        <div className='col-span-4 md:col-span-2'>
                            <Box style='white' padding='large'>
                                <div className='grid items-center grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8'>
                                    <div className='sm:col-span-2'>
                                        <Text h3>{collection.group[1].name}</Text>
                                    </div>
                                    {collection.group[1].pieces.map((piece) => {
                                        featuredMusic += piece.slug
                                        return (
                                            <MusicItem
                                                title={piece.title}
                                                key={piece.slug}
                                                color={piece.color}
                                                slug={piece.slug}
                                                image={buildImage(piece.image)}
                                            />
                                        )
                                    })}
                                    <div className='hidden sm:col-span-2 sm:block'>
                                        <Button
                                            value='View more'
                                            style='text'
                                            glue={['left', 'bottom']}
                                            icon={<ArrowRight />}
                                            iconPosition='after'
                                            color='primary'
                                            href='#other'
                                        />
                                    </div>
                                </div>
                            </Box>
                        </div>
                        <div className='col-span-4 md:col-span-2'>
                            <Box style='white' padding='large'>
                                <div className='relative w-full h-full md:flex md:items-center'>
                                    <div className='top-0 md:absolute'>
                                        <Text h6 as='p' color='text-primary'>
                                            Did you know?
                                        </Text>
                                    </div>
                                    <div className='flex flex-col mx-auto mt-4 space-y-4 md:mt-0'>
                                        <Text h2 as='p' color='text-accent-magenta'>
                                            Quality is important
                                        </Text>
                                        <Text h5 as='p' color='text-gray'>
                                            For every 1 minute of published music, 3 minutes of
                                            finished music were scrapped. Only the best music is
                                            released.
                                        </Text>
                                    </div>
                                </div>
                            </Box>
                        </div>
                        <div className='col-span-4'>
                            <Box style='white' padding='large'>
                                <div className='grid items-center grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4 lg:gap-8'>
                                    <div className='flex flex-col space-y-2'>
                                        <Text h3>{collection.group[2].name}</Text>
                                        <div className='hidden sm:block'>
                                            <Button
                                                value='View more'
                                                style='text'
                                                glue={['left']}
                                                icon={<ArrowRight />}
                                                iconPosition='after'
                                                color='primary'
                                                href='#other'
                                            />
                                        </div>
                                    </div>
                                    {collection.group[2].pieces.map((piece) => {
                                        featuredMusic += piece.slug
                                        return (
                                            <MusicItem
                                                title={piece.title}
                                                key={piece.slug}
                                                color={piece.color}
                                                slug={piece.slug}
                                                image={buildImage(piece.image)}
                                            />
                                        )
                                    })}
                                </div>
                            </Box>
                        </div>
                        <div className='col-span-4 md:col-span-2'>
                            <Box style='white' padding='large'>
                                <div className='relative w-full h-full md:flex md:items-center'>
                                    <div className='top-0 md:absolute'>
                                        <Text h6 as='p' color='text-primary'>
                                            Did you know?
                                        </Text>
                                    </div>
                                    <div className='flex flex-col mx-auto mt-4 space-y-4 md:mt-0'>
                                        <Text h2 as='p' color='text-success'>
                                            Music should be fun
                                        </Text>
                                        <Text h5 as='p' color='text-gray'>
                                            Every piece of music is designed to be interesting to
                                            play. Every part. Every instrument.
                                        </Text>
                                    </div>
                                </div>
                            </Box>
                        </div>
                        <div className='col-span-4 md:col-span-2'>
                            <Box style='white' padding='large'>
                                <div className='grid items-center grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8'>
                                    <div className='sm:col-span-2'>
                                        <Text h3>{collection.group[3].name}</Text>
                                    </div>
                                    {collection.group[3].pieces.map((piece) => {
                                        featuredMusic += piece.slug
                                        return (
                                            <MusicItem
                                                title={piece.title}
                                                key={piece.slug}
                                                color={piece.color}
                                                slug={piece.slug}
                                                image={buildImage(piece.image)}
                                            />
                                        )
                                    })}
                                    <div className='hidden sm:col-span-2 sm:block'>
                                        <Button
                                            value='View more'
                                            style='text'
                                            glue={['left', 'bottom']}
                                            icon={<ArrowRight />}
                                            iconPosition='after'
                                            color='primary'
                                            href='#other'
                                        />
                                    </div>
                                </div>
                            </Box>
                        </div>
                        <div className='col-span-4' id='other'>
                            <Box style='white' padding='large'>
                                <div className='grid items-center grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-4 lg:gap-8'>
                                    <Text h3>Other music</Text>
                                    {allMusic
                                        .sort((a, b) => (a.title > b.title ? 1 : -1))
                                        .map((piece) => {
                                            // eslint-disable-next-line react/jsx-no-useless-fragment -- base case
                                            if (featuredMusic.includes(piece.slug)) return <></>
                                            return (
                                                <MusicItem
                                                    title={piece.title}
                                                    key={piece.slug}
                                                    color={piece.color}
                                                    slug={piece.slug}
                                                    image={buildImage(piece.image)}
                                                />
                                            )
                                        })}
                                </div>
                            </Box>
                        </div>
                        <div className='col-span-4'>
                            <div className='max-w-2xl mx-auto md:py-8'>
                                <Box style='white' padding='large'>
                                    <Text h2>FAQ</Text>
                                    <div className='h-8' />
                                    <AccordionGroup>
                                        <Accordion title='Where is the other music?' small>
                                            <Text>
                                                Unfortunately I can't display all my music here for
                                                various reasons. Sometimes an NDA prevents me from
                                                talking about a project. If it's a commission, I
                                                technically don't own the rights and I don't always
                                                get permission to share it. And others just aren't
                                                here because they are old and not high quality.
                                            </Text>
                                        </Accordion>
                                        <Accordion title='Where can I buy your music?' small>
                                            <TextContent>
                                                <p>
                                                    If it's for sale, you can find it at{' '}
                                                    <Link href='https://partsandscore.com/composer/nick-mazuk'>
                                                        Parts and Score
                                                    </Link>
                                                    . For legal reasons, a few of my arrangements
                                                    are on{' '}
                                                    <Link href='https://www.sheetmusicplus.com/publishers/mazuk-music-sheet-music/3006507'>
                                                        Sheet Music Plus
                                                    </Link>
                                                    . If you can't find what you're looking for,{' '}
                                                    <Link href='/contact'>send me a message</Link>.
                                                </p>
                                            </TextContent>
                                        </Accordion>
                                        <Accordion title='Do you accept commissions?' small>
                                            <TextContent>
                                                <p>
                                                    Yes I do! Just{' '}
                                                    <Link href='/contact'>send me a message</Link>.
                                                </p>
                                            </TextContent>
                                        </Accordion>
                                    </AccordionGroup>
                                </Box>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps = async () => {
    const [collection, allMusic] = await Promise.all([getMusicCollection(), getAllMusic()])
    return {
        props: {
            collection: collection ?? null,
            allMusic: allMusic ?? null,
        },
    }
}
